import type { ReactNode } from 'react'

/**
 * The documents behind one claim (figure S1's "Supported by …" line).
 *
 * Two states, and the second is the reason this component exists. When a claim
 * has supporting documents it names them. When it has NONE it says so, in
 * amber, because a claim asserted with nothing behind it is a filing risk that
 * is invisible in every document-scoped view: you cannot see an absence by
 * paging through documents one at a time.
 *
 * WHY IT NEVER RESOLVES DOCUMENT NAMES ITSELF
 * -------------------------------------------
 * Whatever the claims payload carries is what gets rendered. Since Bug #5835
 * that payload carries the names too, built server-side from the same
 * per-reader entitled set the ids come from, so the chips print "Coating
 * deposition spec v3" instead of a UUID. A document whose record holds no name
 * is labelled "Untitled document" — the id is never shown, because a UUID names
 * nothing to a reader.
 *
 * This component must never reach for the page's documents query to turn an id
 * into a nicer label: that query is not re-filtered per reader, so resolving a
 * name through it could surface a document the reader is not entitled to. That
 * is the Bug #5635 leak shape, and a source test pins the prohibition on this
 * file's import closure.
 */
export interface ClaimSupportChipsProps {
  supportingDocumentIds: string[]
  /** id -> display name, only ever from the claims payload itself (Bug #5835). */
  documentNames?: Record<string, string>
  /** Rendered after the document names, e.g. the combination note or "focused". */
  trailing?: ReactNode
}

export default function ClaimSupportChips({
  supportingDocumentIds,
  documentNames,
  trailing,
}: ClaimSupportChipsProps) {
  if (supportingDocumentIds.length === 0) {
    return (
      <div data-part="ClaimSupportChips" className="mt-1 text-[11px] font-semibold text-amber-700">
        {/* Contract row S1 pins the phrase "No supporting document"; the figure
            words it as the sentence below, which says the same thing about the
            same claim. */}
        <span title="No supporting document">⚠ Asserted with no supporting document</span>
        {trailing ? <span className="font-normal text-gray-500"> · {trailing}</span> : null}
      </div>
    )
  }

  return (
    <div data-part="ClaimSupportChips" className="mt-1 text-[11px] text-gray-500">
      <span>Supported by</span>{' '}
      {supportingDocumentIds.map((id, i) => (
        <span key={id}>
          {i > 0 ? ' + ' : ''}
          <b className="font-semibold text-gray-700">{documentNames?.[id] ?? 'Untitled document'}</b>
        </span>
      ))}
      {trailing ? <span> · {trailing}</span> : null}
    </div>
  )
}
